/**
 * Theme of the web app (APA-01, APA-03; docs/05 §8). Two sources with a clear priority:
 *
 * 1. The **device** preference (localStorage). It is read by the inline script of `index.html`
 *    before the first paint, so there is no flash, and it wins when the user chose
 *    «solo en este dispositivo».
 * 2. The **user** preference (`user_preferences.theme`), which syncs across devices.
 *
 * Changing the theme writes both, unless it is device-only: then the synced preference is left
 * untouched. `system` follows `prefers-color-scheme` live.
 */

export const THEMES = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof THEMES)[number];
export type ResolvedTheme = 'light' | 'dark';

/** Same keys the inline script of `index.html` uses; changing one means changing both. */
export const THEME_STORAGE_KEY = 'flow.theme';
export const THEME_DEVICE_ONLY_KEY = 'flow.theme.deviceOnly';

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

export interface StoredTheme {
  preference: ThemePreference;
  /** The user chose «solo en este dispositivo»: the synced preference must not change. */
  deviceOnly: boolean;
}

function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    // Safari in private mode can throw when the page has no storage access.
    return undefined;
  }
}

export function readStoredTheme(): StoredTheme | undefined {
  const value = storage()?.getItem(THEME_STORAGE_KEY);
  if (!isThemePreference(value)) return undefined;
  return { preference: value, deviceOnly: storage()?.getItem(THEME_DEVICE_ONLY_KEY) === '1' };
}

export function writeStoredTheme({ preference, deviceOnly }: StoredTheme): void {
  const store = storage();
  if (!store) return;
  store.setItem(THEME_STORAGE_KEY, preference);
  if (deviceOnly) store.setItem(THEME_DEVICE_ONLY_KEY, '1');
  else store.removeItem(THEME_DEVICE_ONLY_KEY);
}

export function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return prefersDark() ? 'dark' : 'light';
  return preference;
}

/** Applies the theme to `<html>`, which is what the token variables key off. */
export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference);
  document.documentElement.dataset.theme = resolved;
  return resolved;
}
